import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "../lib/axios.js";

const encodePathSegment = (value) => encodeURIComponent(String(value));

export const fetchUserLeagues = async () => {
  console.log('useGetUserLeagues fetching...');
  const response = await axiosInstance.get('/leagues?memberOnly=true');
  const userLeagues = response.data.leagues.filter(league => league.isUserMember);
  console.log('useGetUserLeagues fetch complete, found leagues:', userLeagues);
  return userLeagues;
};

export const useCreateLeague = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["create-league"],
    mutationFn: async (formData) => {
      try {
        // Do NOT override headers here – axios will set the proper multipart boundary and
        // the interceptor can attach the Authorization token. We only keep withCredentials.
        const response = await axiosInstance.post("/leagues", formData, {
          withCredentials: true,
        });
        
        console.log('Server response data:', response.data);
        return response.data;
      } catch (error) {
        console.error('Error in API call:', error);
        if (error.response) {
          console.error('Response error data:', error.response.data);
          console.error('Response error status:', error.response.status);
          console.error('Response error headers:', error.response.headers);
        } else if (error.request) {
          console.error('No response received:', error.request);
        } else {
          console.error('Error setting up request:', error.message);
        }
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('League created successfully:', data);
      queryClient.invalidateQueries({ queryKey: ['user-leagues'] });
      // TODO: Consider navigation logic here or in the component
      // e.g., if data.slug or data.league.slug is available: navigate(`/league/${data.slug}/table`);
    },
    onError: (error) => {
      console.error('Mutation error:', error);
    }
  });
};

export const useGetLeagueBySlug = (slug, enabled = true) => {
  return useQuery({
    // Include slug to prevent stale cache when switching leagues
    queryKey: ["league", slug],
    queryFn: async () => {
      const response = await axiosInstance.get(`/leagues/${encodePathSegment(slug)}`);
      return response;
    },
    enabled: !!slug && enabled,
  });
};

export const useGetUserLeagues = (enabled = true) => {
  return useQuery({
    queryKey: ["user-leagues"],
    queryFn: fetchUserLeagues,
    enabled: enabled,
    retry: false
  });
};

export const useCreateLeagueInviteLink = () => {
  return useMutation({
    mutationKey: ["league-invite-link"],
    mutationFn: async (leagueId) => {
      const response = await axiosInstance.post(
        `/leagues/${encodePathSegment(leagueId)}/invite-link`
      );
      return response.data;
    }
  });
};

export const useGenerateFixtures = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["generate-fixtures"],
    mutationFn: async (leagueId) => {
      try {
        const response = await axiosInstance.post(`/leagues/${leagueId}/generate-fixtures`, {}, {
        });
        return response.data;
      } catch (error) {
        console.error('Error generating fixtures:', error);
        throw error;
      }
    },
    onSuccess: () => {
      // Fixture generation changes league matches/season, refresh cached league data
      queryClient.invalidateQueries({ queryKey: ['league'] });
      queryClient.invalidateQueries({ queryKey: ['league-fixtures'] });
    },
  });
};

export const useUpdateLeague = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-league"],
    mutationFn: async ({ leagueId, formData }) => {
      try {
        // PATCH request with multipart/form-data or JSON
        // Axios will automatically set the correct Content-Type for FormData
        const response = await axiosInstance.patch(`/leagues/${leagueId}`, formData, {
          withCredentials: true,
        });
        
        console.log('League updated successfully:', response.data);
        return response.data;
      } catch (error) {
        console.error('Error updating league:', error);
        if (error.response) {
          console.error('Response error data:', error.response.data);
          console.error('Response error status:', error.response.status);
        }
        throw error;
      }
    },
    onMutate: async ({ leagueId, formData, slug }) => {
      // Cancel any outgoing refetches to prevent optimistic update overwrites
      await queryClient.cancelQueries({ queryKey: ['league', slug] });
      
      // Snapshot the previous value
      const previousLeague = queryClient.getQueryData(['league', slug]);
      
      // Optimistically update the cache with new data
      if (previousLeague?.data?.league) {
        const optimisticLeague = { ...previousLeague };
        
        // Extract form data for optimistic update
        if (formData instanceof FormData) {
          const name = formData.get('name');
          const amountOfTeams = formData.get('amountOfTeams');
          const freeJoin = formData.get('freeJoin');
          const matchKickoffTime = formData.get('matchKickoffTime');
          
          if (name) optimisticLeague.data.league.name = name;
          if (amountOfTeams) optimisticLeague.data.league.amountOfTeams = parseInt(amountOfTeams);
          if (freeJoin !== null) optimisticLeague.data.league.freeJoin = freeJoin === 'true';
          if (matchKickoffTime) optimisticLeague.data.league.matchKickoffTime = matchKickoffTime;
        }
        
        queryClient.setQueryData(['league', slug], optimisticLeague);
      }
      
      // Return a context object with the snapshotted value
      return { previousLeague, slug };
    },
    onSuccess: (data, variables) => {
      console.log('League update successful:', data);
      // Invalidate league-related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['league', data.league?.slug] });
      queryClient.invalidateQueries({ queryKey: ['user-leagues'] });
      queryClient.invalidateQueries({ queryKey: ['all-leagues'] });
    },
    onError: (error, variables, context) => {
      console.error('Mutation error:', error);
      // If we have a context, rollback to the previous value
      if (context?.previousLeague && context?.slug) {
        queryClient.setQueryData(['league', context.slug], context.previousLeague);
      }
    }
  });
};

export const useInviteToLeague = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["invite-to-league"],
    mutationFn: async ({ leagueId, emails }) => {
      try {
        const response = await axiosInstance.post(`/leagues/${leagueId}/invite`, {
          emails: emails
        }, {
          withCredentials: true,
        });
        
        console.log('Invites sent successfully:', response.data);
        return response.data;
      } catch (error) {
        console.error('Error sending invites:', error);
        if (error.response) {
          console.error('Response error data:', error.response.data);
          console.error('Response error status:', error.response.status);
        }
        throw error;
      }
    },
    onMutate: async ({ leagueId, emails, slug }) => {
      // Cancel any outgoing refetches to prevent optimistic update overwrites
      await queryClient.cancelQueries({ queryKey: ['league', slug] });
      
      // Snapshot the previous value
      const previousLeague = queryClient.getQueryData(['league', slug]);
      
      // Note: For invites, we don't do optimistic updates to the league data
      // since invite status is not immediately reflected in league membership
      // The optimistic context is mainly for rollback purposes
      
      // Return a context object with the snapshotted value
      return { previousLeague, slug };
    },
    onSuccess: (data, variables) => {
      console.log('Invites sent successfully:', data);
      // Invalidate league-related queries to refresh data  
      queryClient.invalidateQueries({ queryKey: ['league', variables.slug] });
      queryClient.invalidateQueries({ queryKey: ['user-leagues'] });
      queryClient.invalidateQueries({ queryKey: ['all-leagues'] });
    },
    onError: (error, variables, context) => {
      console.error('Invite mutation error:', error);
      // For invite errors, we don't need to rollback since we didn't optimistically update
      // But we ensure the cache is in a consistent state
      if (context?.slug) {
        queryClient.invalidateQueries({ queryKey: ['league', context.slug] });
      }
    }
  });
};

export const useGetInvite = (token) => {
  return useQuery({
    queryKey: ['invite', token],
    enabled: !!token,
    retry: false,
    queryFn: async () => {
      const response = await axiosInstance.get(`/leagues/invite/${encodeURIComponent(token)}`);
      return response.data;
    }
  });
};

export const useAcceptInvite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['accept-invite'],
    mutationFn: async (token) => {
      const response = await axiosInstance.post(
        `/leagues/invite/${encodeURIComponent(token)}/accept`,
        {},
        { withCredentials: true }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-leagues'] });
      queryClient.invalidateQueries({ queryKey: ['all-leagues'] });
    }
  });
};

export const useKickFromLeague = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["kick-from-league"],
    mutationFn: async ({ leagueId, teamId }) => {
      try {
        const response = await axiosInstance.delete(`/leagues/${leagueId}/teams/${teamId}`, {
          withCredentials: true,
        });
        
        console.log('Team removed successfully:', response.data);
        return response.data;
      } catch (error) {
        console.error('Error removing team:', error);
        if (error.response) {
          console.error('Response error data:', error.response.data);
          console.error('Response error status:', error.response.status);
        }
        throw error;
      }
    },
    onMutate: async ({ leagueId, teamId, slug }) => {
      // Cancel any outgoing refetches to prevent optimistic update overwrites
      await queryClient.cancelQueries({ queryKey: ['league', slug] });
      
      // Snapshot the previous value
      const previousLeague = queryClient.getQueryData(['league', slug]);
      
      // Optimistically remove the team from the league
      if (previousLeague?.data?.league?.teams) {
        const optimisticLeague = { ...previousLeague };
        optimisticLeague.data.league.teams = optimisticLeague.data.league.teams.filter(
          team => team._id !== teamId
        );
        // Update available slots
        if (optimisticLeague.data.league.availableSlots !== undefined) {
          optimisticLeague.data.league.availableSlots += 1;
        }
        
        queryClient.setQueryData(['league', slug], optimisticLeague);
      }
      
      // Return a context object with the snapshotted value
      return { previousLeague, slug };
    },
    onSuccess: (data, variables) => {
      console.log('Team removal successful:', data);
      // Invalidate league-related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['league', variables.slug] });
      queryClient.invalidateQueries({ queryKey: ['user-leagues'] });
      queryClient.invalidateQueries({ queryKey: ['all-leagues'] });
    },
    onError: (error, variables, context) => {
      console.error('Kick mutation error:', error);
      // If we have a context, rollback to the previous value
      if (context?.previousLeague && context?.slug) {
        queryClient.setQueryData(['league', context.slug], context.previousLeague);
      }
    }
  });
};
